import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';

import moment from 'moment';

import selectAllEntitiesIds from '../../../../../reducers/Metrics/selector/selectAllEntitiesIds';

import {getRisks} from '../../../../../actions/GroupRiskAssessment';
import {getActivityLogExportData} from '../../../../../actions/ActivityLog';

import {currentResource, labels} from '../../../../../util/utils';
import {
	ACTIVITY_FEED_FILE_NAME,
	FILE_NAME_DATE_FORMAT,
	clientRequestHeadersActivityFeed,
	clientRequestHeadersEngagement,
	ENGAGEMENT_FILE_NAME,
	exportOptions
} from '../GenerateExcel/utils/constants';

import Button from '../../../../CoreWrappers/Button';
import Dropdown from '../../../../CoreWrappers/Dropdown';
import StyledGenerateExcel from './StyledGenerateExcel';
import StyledGenerateExcelWrapper from './StyledGenerateExcelWrapper';
import {generateExcelWithTopDetails} from '../../../../Common/ExportToExcel/ExportWithTopDetails/utils/generateExcelWithTopDetails';

import {
	getAccountTypeName,
	getInherentRiskTypeName,
	getControlRiskTypeName,
	getRiskTypeName,
	getTaskStatusName
} from '../../../CreateProjectGRA/GenerateExcelCommon/Utils';

const GenerateExcel = () => {
	const dispatch = useDispatch();
	const exportToExcelLabels = currentResource.exportToExcelLabels;
	const [isExporting, setIsExporting] = useState(false);
	const [option, setOption] = useState(null);
	const project = useSelector((store) => store.project);
	const engagementData = useSelector((store) => store.risks);
	const activityLogExportData = useSelector((store) => store.activityLog.exportData);
	const [userDateFormat, userTimezone] = useSelector(({userPreferences}) => [
		userPreferences.dateFormat,
		userPreferences.timeZoneISOCode
	]);
	const entities = selectAllEntitiesIds();

	//Need this to specificaly handle the delay in redux getting updated after api call (api call is being made on click of export) and getting updated data in exported file.
	useEffect(() => {
		if (isExporting === true && option !== null) {
			if (option === exportOptions.activityFeed) {
				generateExcelWithTopDetails(
					userDateFormat,
					userTimezone,
					exportToExcelLabels,
					getExportDetails(exportOptions.activityFeed),
					setIsExporting
				);
			}
			if (option === exportOptions.engagementData) {
				generateExcelWithTopDetails(
					userDateFormat,
					userTimezone,
					exportToExcelLabels,
					getExportDetails(exportOptions.engagementData),
					setIsExporting
				);
			}
			setOption(null);
		}
	}, [activityLogExportData, engagementData]);

	const getExportDetails = (selectedExportOption) => {
		let name = selectedExportOption === exportOptions.activityFeed ? ACTIVITY_FEED_FILE_NAME : ENGAGEMENT_FILE_NAME;
		return {
			projectName: project.name,
			fileName: `${project.name}${name}${moment()
				.utc()
				.format(FILE_NAME_DATE_FORMAT)}`,
			title: labels.canvasClientPortalEy,
			columns: getColumnsToExport(selectedExportOption),
			data: getDataToExport(selectedExportOption),
			worksheetName:
				selectedExportOption === exportOptions.activityFeed ? labels.activityFeed : labels.engagementDetails
		};
	};

	const getColumnsToExport = (exportOption) => {
		let columns = [];
		if (exportOption === exportOptions.activityFeed) {
			columns.push({
				header: currentResource.activityTableLabels.sourceEngagement,
				key: clientRequestHeadersActivityFeed.sourceEngagement
			});
			columns.push({
				header: currentResource.activityTableLabels.activityType,
				key: clientRequestHeadersActivityFeed.activityType
			});
			columns.push({
				header: currentResource.activityTableLabels.objectImpacted,
				key: clientRequestHeadersActivityFeed.objectImpacted
			});
			columns.push({
				header: currentResource.activityTableLabels.relatedObjectImpacted,
				key: clientRequestHeadersActivityFeed.relatedObjectImpacted
			});
			columns.push({
				header: currentResource.activityTableLabels.relatedSecondaryObjectImpacted,
				key: clientRequestHeadersActivityFeed.relatedSecondaryObjectImpacted
			});
			columns.push({
				header: currentResource.activityTableLabels.activityDate,
				key: clientRequestHeadersActivityFeed.activityDate
			});
		} else {
			columns.push({
				header: currentResource.engagementLabels.engagementDescription,
				key: clientRequestHeadersEngagement.engagementDescription
			});
			columns.push({
				header: currentResource.engagementLabels.canvasEngagementId,
				key: clientRequestHeadersEngagement.canvasEngagementId
			});
			columns.push({
				header: currentResource.engagementLabels.accountName,
				key: clientRequestHeadersEngagement.accountName
			});
			columns.push({
				header: currentResource.engagementLabels.pspIndexTitle,
				key: clientRequestHeadersEngagement.pspIndexTitle
			});
			columns.push({
				header: currentResource.engagementLabels.accountTypeName,
				key: clientRequestHeadersEngagement.accountTypeName
			});
			columns.push({
				header: currentResource.engagementLabels.assertionName,
				key: clientRequestHeadersEngagement.assertionName
			});
			columns.push({
				header: currentResource.engagementLabels.inherentRiskTypeName,
				key: clientRequestHeadersEngagement.inherentRiskTypeName
			});
			columns.push({
				header: currentResource.engagementLabels.controlRiskTypeName,
				key: clientRequestHeadersEngagement.controlRiskTypeName
			});
			columns.push({
				header: currentResource.engagementLabels.controlRiskAssessment,
				key: clientRequestHeadersEngagement.controlRiskAssessment
			});
			columns.push({
				header: currentResource.engagementLabels.riskTypeName,
				key: clientRequestHeadersEngagement.riskTypeName
			});
			columns.push({header: currentResource.engagementLabels.riskName, key: clientRequestHeadersEngagement.riskName});
			columns.push({
				header: currentResource.engagementLabels.relatedTasks,
				key: clientRequestHeadersEngagement.relatedTasks
			});
			columns.push({
				header: currentResource.engagementLabels.taskStatus,
				key: clientRequestHeadersEngagement.taskStatus
			});
		}
		return columns;
	};

	const getDataToExport = (exportOption) => {
		if (exportOption === exportOptions.activityFeed) {
			return activityLogExportData?.map((item) => {
				return {...item, activityType: currentResource.activityLogTypes[item.activityType]};
			});
		} else {
			let data = [];
			engagementData?.forEach((element) => {
				data.push({
					engagementDescription: element.engagementDescription,
					canvasEngagementId: element.canvasEngagementId,
					accountName: element.accountName,
					pspIndexTitle: element.pspIndexTitle,
					accountTypeName: getAccountTypeName(element.accountTypeId),
					assertionName:
						(element.assertionName === undefined ? '' : element.assertionName) +
						(element.assertionAbbreviation === undefined ? '' : ' (' + element.assertionAbbreviation + ')'),
					inherentRiskTypeName: getInherentRiskTypeName(element.inherentRiskTypeId),
					controlRiskTypeName: getControlRiskTypeName(element.controlRiskTypeId),
					controlRiskAssessment: element.craValue,
					riskTypeName: getRiskTypeName(element.riskTypeId),
					riskName: element.riskName,
					taskStatus: getTaskStatusName(element.taskStatusId),
					relatedTasks:
						(element.taskTypeId === undefined ? '' : element.taskTypeId) +
						(element.taskNamePrefix === undefined ? '' : '|' + element.taskNamePrefix) +
						(element.knowledgeTaskTitle === undefined
							? element.taskName === undefined
								? ''
								: '|' + element.taskName
							: '|' + element.knowledgeTaskTitle) +
						(element.taskNameSuffix === undefined ? '' : '|' + element.taskNameSuffix)
				});
			});
			return data;
		}
	};

	const exportActivityLogData = () => {
		if (!isExporting) {
			setIsExporting(true);
			setOption(exportOptions.activityFeed);
			dispatch(getActivityLogExportData(project.projectId, entities));
		}
	};

	const exportEngagementData = () => {
		if (!isExporting) {
			setIsExporting(true);
			setOption(exportOptions.engagementData);
			let args = [project.projectId, entities];
			dispatch(getRisks(...args));
		}
	};

	const menuItems = [
		{id: 1, label: labels.activityFeed, onClick: () => exportActivityLogData()},
		{id: 2, label: labels.engagementData, onClick: () => exportEngagementData()}
	];

	const renderTrigger = (label = exportToExcelLabels.exportData) => {
		return (
			<StyledGenerateExcel className="export-btn-wrap">
				<Button variant={'text-alt'} label={label} disabled={isExporting} />
			</StyledGenerateExcel>
		);
	};

	const props = {
		topfix: true,
        reversed: true,
		items: menuItems,
		trigger: renderTrigger(),
		placement: 'bottom-right'
	};

	return (
		<StyledGenerateExcelWrapper>
			<Dropdown {...props} />
		</StyledGenerateExcelWrapper>
	);
};

export default GenerateExcel;
